/**
 * Public landing page — the marketing front door for Chillet's Palworld
 * community. Lives at `/` for both logged-in and anonymous visitors.
 *
 * Sections (top to bottom):
 *   1. Hero with the fan-art servers banner + dual CTA (Discord + Sign in/Dashboard)
 *   2. Live server status strip — both Palworld servers, pulled from /api/servers/status
 *   3. Feature grid — what the website offers, links into the app
 *   4. Discord bot section — what the bot brings to the table
 *   5. Discord invite card — neon banner art + invite button
 *   6. Footer credit line
 *
 * Visual rule: keep the gothic-teal palette, but lean into Palworld's pink
 * Chillet mascot for warmth. Three pal renders float in as decorative
 * accents so the page doesn't read as a wall of cards.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Map as MapIcon,
  Coins,
  ShoppingBag,
  Swords,
  Dice5,
  Search,
  Castle,
  Sparkles,
  ExternalLink,
  CircleDot,
  Bot,
  ShieldCheck,
  Gift,
  HeartHandshake,
  Bell,
  Wallet,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { api, API, getShared } from "@/lib/api";

const DISCORD_INVITE = "https://discord.gg/jHABQMPF4P";

/** Fire-and-forget click counter — POST goes to admin_audit_log via
 * /api/metrics/discord-click. `cta` distinguishes which surface was
 * clicked so the admin Action Feed can show e.g. "hero" vs "invite_card".
 * We deliberately do NOT await before navigating to Discord so a slow
 * request never blocks the open. */
function pingDiscordClick(cta) {
  try {
    api.post(`/metrics/discord-click?cta=${encodeURIComponent(cta || "unknown")}`).catch(() => {});
  } catch {
    /* noop */
  }
}

const FEATURE_TILES = [
  {
    to: "/map",
    icon: MapIcon,
    title: "Interactive World Map",
    blurb:
      "133,000+ markers across Palpagos & Feybreak — Alpha Pals, Predators, Bounties, Lifmunk Effigies, Anti-Air Turrets, and your live in-game position.",
    color: "var(--teal)",
  },
  {
    to: "/towers",
    icon: Castle,
    title: "Boss Towers",
    blurb:
      "Seven syndicate towers with stats, signature moves, weakness charts, and one-click Discord 'Looking for Group' callouts.",
    color: "#FF6B35",
  },
  {
    to: "/palgame",
    icon: Swords,
    title: "Pal Arena & PvP",
    blurb:
      "4 NPC arena tiers, ranked PvP battles with skull wagers in escrow, expeditions, fusion, and player-to-player pal trading.",
    color: "#FFD700",
  },
  {
    to: "/lookup",
    icon: Search,
    title: "Paldex Wiki",
    blurb:
      "Native browser for every pal, item, structure & tech recipe — 1969 items, 663 pals, 502 tech, all searchable and filterable.",
    color: "#9D4CDD",
  },
  {
    to: "/economy",
    icon: Coins,
    title: "Chillet Coins",
    blurb:
      "Daily rewards, VIP streaks, transfers between players, and live balance updates pushed straight from the server.",
    color: "#FFD700",
  },
  {
    to: "/shop",
    icon: ShoppingBag,
    title: "Shop & Kits",
    blurb:
      "Spend skulls on items, kits, and pal eggs delivered straight into your inventory via the in-game RCON bridge.",
    color: "#A9A2B5",
  },
  {
    to: "/gambling",
    icon: Dice5,
    title: "Gambling Hall",
    blurb: "Three games of pure chance for the brave — high roll, parlay your way to legendary balances.",
    color: "#E63946",
  },
  {
    to: "/dashboard",
    icon: Sparkles,
    title: "Daily Treasure Hunt",
    blurb:
      "Three coordinates a day, position-verified by the game server. Visit them in-game to auto-claim skulls + streak multipliers.",
    color: "#2A9D8F",
  },
];

const BOT_BULLETS = [
  {
    icon: ShoppingBag,
    title: "In-game purchases via Discord",
    text: "Shop on the website or by chat — items and pals get RCON-delivered the moment you spawn in.",
  },
  {
    icon: Gift,
    title: "Live giveaways with auto prize delivery",
    text: "Mod-hosted giveaways pick a winner and push the prize straight to their character — no manual handoff.",
  },
  {
    icon: Wallet,
    title: "Daily skull rewards & streaks",
    text: "Claim once a day on Discord or the website — both halves stay in lockstep, streak multiplier scales to 2×.",
  },
  {
    icon: Bell,
    title: "Server status pings",
    text: "Restarts, raids, and special events get announced in your Discord channels with live FPS and player counts.",
  },
  {
    icon: HeartHandshake,
    title: "PvP challenges & pal trading",
    text: "Challenge friends to ranked battles, swap pals safely with 5% house-tax escrow, all settled in chat.",
  },
];

function ServerStatusCard({ matchNames, displayName, accent, testid }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    getShared("/servers/status")
      .then((data) => {
        if (!alive) return;
        const lc = matchNames.map((n) => n.toLowerCase());
        const found = (data?.servers || []).find((s) =>
          lc.includes((s.name || "").toLowerCase()),
        );
        setData(found || null);
      })
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
    };
  }, [matchNames]);

  const online = !!data?.online;
  return (
    <div
      className="glass-card p-5 flex-1 min-w-[260px]"
      style={{ borderColor: `${accent}55`, boxShadow: `inset 0 0 32px ${accent}11` }}
      data-testid={`home-server-card-${testid}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3
          className="text-base"
          style={{ fontFamily: "'Cinzel Decorative', serif", color: accent }}
        >
          {displayName}
        </h3>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] flex items-center gap-1.5 ${
            online ? "text-emerald-300" : "text-[var(--muted)]"
          }`}
        >
          <CircleDot
            size={10}
            className={online ? "animate-pulse" : ""}
            style={{ color: online ? "#34d399" : "#666" }}
          />
          {err ? "—" : online ? "Online" : "Offline"}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center mt-3">
        <div>
          <div className="text-xl text-[var(--bone)]" data-testid={`home-${testid}-players`}>
            {data ? `${data.player_count ?? 0}/${data.max_players ?? 32}` : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Players</div>
        </div>
        <div>
          <div className="text-xl text-[var(--bone)]">{data?.fps ? Math.round(data.fps) : "—"}</div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">Server FPS</div>
        </div>
        <div>
          <div className="text-xl text-[var(--bone)]">{data?.in_game_days ?? "—"}</div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">In-game days</div>
        </div>
      </div>
    </div>
  );
}

function FeatureTile({ to, icon: Icon, title, blurb, color }) {
  return (
    <Link
      to={to}
      data-testid={`home-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="glass-card p-5 transition-transform hover:-translate-y-0.5 group"
      style={{ borderColor: `${color}33` }}
    >
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center mb-3 transition-all group-hover:scale-110"
        style={{
          background: `${color}1a`,
          color,
          border: `1px solid ${color}55`,
          boxShadow: `0 0 12px ${color}33`,
        }}
      >
        <Icon size={18} />
      </div>
      <h3 className="text-sm uppercase tracking-[0.12em] mb-1.5 text-[var(--bone)]">{title}</h3>
      <p className="text-sm text-[var(--muted)] leading-relaxed">{blurb}</p>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  // Anonymous visitors get a one-click Discord OAuth CTA — bounces
  // straight to /api/auth/discord which initiates the full OAuth dance.
  // Successful logins land on the dashboard; cancels show a friendly
  // toast via the new /login?oauth_error= handling.
  const loggedOutPrimary = `${API}/auth/discord`;
  const loggedInPrimary = "/dashboard";

  // Match the "Discord" click in the admin Action Feed so we can later
  // measure conversion: anonymous-hero-discord-cta → /api/auth/discord
  // hits → /api/auth/discord/callback successes.
  const onAnonDiscordLogin = () => {
    try {
      api.post("/metrics/discord-click?cta=hero-login").catch(() => {});
    } catch { /* never block nav */ }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-12 space-y-16">
      {/* ====================== HERO ====================== */}
      <section className="relative" data-testid="home-hero">
        <div
          className="relative rounded-2xl overflow-hidden border border-[var(--teal-soft)]"
          style={{
            // Subtle backdrop so the square poster has presence without
            // looking like it's floating awkwardly on a wide hero band.
            background:
              "radial-gradient(ellipse at center, rgba(0,229,255,0.10), rgba(6,3,10,0.95) 70%)",
            boxShadow: "inset 0 0 80px rgba(0,229,255,0.08)",
          }}
        >
          <img
            src="/assets/home/servers_banner.png"
            alt="Chillet's Modded Demise and Chillet's Revival PvPvE — community fan art"
            className="block mx-auto"
            style={{
              maxHeight: 560,
              maxWidth: "100%",
              width: "auto",
              padding: "1.5rem 1rem 0.5rem 1rem",
              filter: "drop-shadow(0 0 24px rgba(0,229,255,0.35))",
            }}
          />
          {/* Bottom fade so the card below visually attaches to the art */}
          <div
            className="absolute inset-x-0 bottom-0 h-20 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(6,3,10,0) 0%, rgba(6,3,10,0.7) 70%, rgba(6,3,10,0.95) 100%)",
            }}
          />
        </div>

        <div className="relative -mt-6 mx-2 sm:mx-8 glass-card p-6 sm:p-8 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="max-w-2xl">
              <h1
                className="glow-teal mb-3"
                style={{
                  fontFamily: "'Cinzel Decorative', serif",
                  letterSpacing: "0.04em",
                  fontSize: "clamp(1.6rem, 4vw, 2.6rem)",
                  lineHeight: 1.15,
                }}
              >
                A Palworld community — two servers, one portal.
              </h1>
              <p className="text-base text-[var(--muted)] leading-relaxed">
                <span className="text-[var(--bone)]">Better with friends.</span> Chillet's runs
                two modded servers, a Discord bot that lives in your chat, and this companion
                portal that ties it all together — interactive map, pal trading, daily hunts,
                arena battles, and a real-time skull economy.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center sm:justify-end">
              <a
                href={DISCORD_INVITE}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => pingDiscordClick("hero")}
                className="btn"
                style={{
                  borderColor: "#5865F2",
                  color: "#5865F2",
                  boxShadow: "0 0 16px rgba(88,101,242,0.35)",
                }}
                data-testid="home-discord-cta"
              >
                <ExternalLink size={14} /> Join the Discord
              </a>
              {user ? (
                <Link to={loggedInPrimary} className="btn" data-testid="home-primary-cta">
                  Open Dashboard
                </Link>
              ) : (
                <a
                  // One-click OAuth login — skips the /login page entirely.
                  // Backend supports graceful cancel as of 2026-02-22, so
                  // hitting "Cancel" on Discord lands users on /login with
                  // a friendly toast instead of a JSON error page.
                  href={loggedOutPrimary}
                  onClick={onAnonDiscordLogin}
                  className="btn btn-cta"
                  style={{
                    background: "linear-gradient(135deg, #5865F2 0%, #4752C4 100%)",
                    borderColor: "#5865F2",
                    color: "#fff",
                    boxShadow: "0 0 24px rgba(88,101,242,0.55)",
                  }}
                  data-testid="home-discord-login-cta"
                >
                  <Bot size={14} /> Sign in with Discord
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ====================== LIVE SERVER STATUS ====================== */}
      <section data-testid="home-server-status">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h2
            className="text-xl sm:text-2xl text-[var(--bone)]"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            Live Right Now
          </h2>
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
            Auto-refreshed from RCON
          </span>
        </div>
        <div className="flex flex-wrap gap-4">
          <ServerStatusCard
            testid="modded-demise"
            matchNames={["Chillet's Modded Demise"]}
            displayName="Chillet's Modded Demise"
            accent="#FF6B35"
          />
          <ServerStatusCard
            testid="revival-pvpve"
            matchNames={["Chillet's Revival PvPvE", "Chillet's Revival Pvpve"]}
            displayName="Chillet's Revival PvPvE"
            accent="#00E5FF"
          />
        </div>
      </section>

      {/* ====================== FEATURE GRID ====================== */}
      <section data-testid="home-features" className="relative">
        {/* Floating decorative Chillet — perched in the gutter to the
            RIGHT of the features section on wide screens. Gentle bob
            animation makes the running pose feel like a real loop. */}
        <img
          src="/assets/home/chillet_running.png"
          alt=""
          aria-hidden="true"
          className="hidden xl:block absolute -top-12 -right-40 2xl:-right-56 w-44 2xl:w-52 opacity-95 pointer-events-none select-none z-10 mascot-bob"
          style={{ filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 24px rgba(0,229,255,0.55))" }}
        />
        <div className="mb-6">
          <h2
            className="text-xl sm:text-2xl text-[var(--bone)] mb-1"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            What's Inside the Portal
          </h2>
          <p className="text-sm text-[var(--muted)]">
            Eight tools built 1:1 with the in-game data, all linked to your Discord identity.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
          {FEATURE_TILES.map((f) => (
            <FeatureTile key={f.title} {...f} />
          ))}
        </div>
      </section>

      {/* ====================== DISCORD BOT ====================== */}
      <section className="relative" data-testid="home-bot">
        {/* Decorative Chillet mascot — sits in the left gutter next to
            the heading "hugging" the title from the side. Body sits in
            the gutter and the front paws/head reach toward the words
            without overlapping them, plus a subtle breathing animation
            so the mascot feels alive. */}
        <img
          src="/assets/home/chillet_fixed.png"
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute -top-6 -left-28 xl:-left-32 w-36 xl:w-40 opacity-95 pointer-events-none select-none z-10 mascot-breathe"
          style={{
            filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 22px rgba(0,229,255,0.55))",
          }}
        />
        <div className="glass-card p-6 sm:p-8 relative overflow-hidden">
          <div className="flex items-center gap-3 mb-1">
            <Bot size={22} className="text-[var(--teal)]" />
            <span className="text-xs uppercase tracking-[0.22em] text-[var(--teal)]">
              Hand-in-hand with the website
            </span>
          </div>
          <h2
            className="text-xl sm:text-2xl text-[var(--bone)] mb-4"
            style={{ fontFamily: "'Cinzel Decorative', serif" }}
          >
            The Chillet's Discord Bot
          </h2>
          <p className="text-sm text-[var(--muted)] leading-relaxed mb-6 max-w-3xl">
            The bot and this website share a single database — every skull you earn, every pal
            you catch, every trade you finalize stays in sync no matter which surface you used.
            Live on the server, settle on Discord, browse on the web.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BOT_BULLETS.map((b) => (
              <div
                key={b.title}
                className="flex gap-3 p-3 rounded-md border border-white/5 bg-black/20"
              >
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(0,229,255,0.08)",
                    color: "var(--teal)",
                    border: "1px solid rgba(0,229,255,0.25)",
                  }}
                >
                  <b.icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-[var(--bone)] mb-0.5">{b.title}</div>
                  <div className="text-xs text-[var(--muted)] leading-relaxed">{b.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== DISCORD INVITE ====================== */}
      <section data-testid="home-discord-invite">
        <div
          className="glass-card p-6 sm:p-8 relative overflow-hidden text-center"
          style={{
            borderColor: "rgba(88,101,242,0.35)",
            background:
              "radial-gradient(ellipse at top, rgba(88,101,242,0.12), transparent 60%), rgba(6,3,10,0.6)",
          }}
        >
          <img
            src="/assets/home/discord_banner.png"
            alt="Welcome to Chillet's Discord Server"
            className="mx-auto w-full max-w-md h-auto mb-6"
            style={{ filter: "drop-shadow(0 0 24px rgba(0,229,255,0.45))" }}
          />
          <p className="text-base text-[var(--muted)] leading-relaxed max-w-xl mx-auto mb-6">
            Patch notes, giveaways, server alerts, raid call-outs, and a community that's
            actually online. Pop in, link your in-game character, and start earning skulls.
          </p>
          <a
            href={DISCORD_INVITE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => pingDiscordClick("invite_card")}
            className="btn inline-flex"
            style={{
              borderColor: "#5865F2",
              color: "#5865F2",
              boxShadow: "0 0 24px rgba(88,101,242,0.4)",
              padding: "0.85rem 2rem",
              fontSize: "0.85rem",
            }}
            data-testid="home-discord-invite-button"
          >
            <ShieldCheck size={16} /> Join discord.gg/jHABQMPF4P
          </a>
          <div className="mt-4 text-[11px] text-[var(--muted)] tracking-wider">
            Free • No download required • Connect to both servers
          </div>
        </div>
      </section>

      {/* ====================== FOOTER ====================== */}
      <footer className="text-center text-[11px] text-[var(--muted)] tracking-wider pt-6 border-t border-white/5">
        <div className="flex items-center justify-center gap-2 mb-1">
          <img
            src="/assets/home/girl_on_chillet.png"
            alt=""
            aria-hidden="true"
            className="w-10 h-10 opacity-70"
          />
          <span>
            ChillPalServers — fan-made, not affiliated with or endorsed by Pocketpair Inc.
          </span>
        </div>
        <span>
          Map data © Palworld Wiki & palworld.th.gl, CC-BY-SA. Built for the community.
        </span>
      </footer>
    </div>
  );
}
